/**
 * Helpers for triggering a Formik `submitForm()` from a UI control.
 *
 * The create/update project and task submit paths report their own error
 * (toast on web, Alert/ConfirmModal on native) and then reject, so the
 * bottom-sheet callers can skip their success-only cleanup and keep the
 * user's input for a retry — see `runTaskCreateSubmit` /
 * `runProjectCreateSubmit`. Every other trigger still has to consume that
 * rejection, otherwise React Native and browsers report it as an unhandled
 * promise rejection.
 */

type SubmitForm = () => Promise<unknown> | void;

// Promise.resolve wraps a submitForm that returns nothing (some callers get
// it as a plain prop); the try/catch covers a synchronous throw, which would
// escape before Promise.resolve ever ran.
const runSubmit = (submitForm: SubmitForm, onSuccess?: () => void) => {
  let submitted: Promise<unknown>;
  try {
    submitted = Promise.resolve(submitForm());
  } catch {
    // Synchronous throw — same contract as a rejection: the submit path has
    // already reported the error, and this trigger owns no cleanup.
    return Promise.resolve();
  }
  // The rejection handler is the second argument to `then`, not a trailing
  // `.catch()`: a trailing catch would also swallow an error thrown inside
  // onSuccess, masking a bug in the cleanup as a submit failure (same
  // reasoning as taskCreateBottomSheetSubmit.ts).
  return submitted.then(
    () => onSuccess?.(),
    () => {
      // Submit failed; the submit path has already reported it.
    }
  );
};

/**
 * For a trigger with nothing to do once the submit finishes: fire and forget,
 * dropping the (already reported) failure.
 */
export const submitFormIgnoringRejection = (submitForm: SubmitForm) =>
  runSubmit(submitForm);

/**
 * For a trigger that closes the form afterwards: `onSuccess` runs only when
 * the submit actually landed, so a failed save leaves the form open with the
 * user's input instead of discarding it.
 */
export const submitFormThenOnSuccess = (
  submitForm: SubmitForm,
  onSuccess: () => void
) => runSubmit(submitForm, onSuccess);
